/**
 * Single source of truth for building "/pdf-sandbox" URLs that point at an
 * SCG rule's source PDF with the rule text highlighted.
 *
 * Accepts a flexible input shape so it works in every caller:
 *   - ManageSCG rule rows (rule object has source_document + location_metadata directly)
 *   - SCGCitation / RuleHoverCard (use the sourceInfo blob returned by /scg/rule-source-info)
 *   - Any caller that only has a rule_id (then sourceInfo must be fetched separately)
 *
 * The PDF URL itself always goes through getSCGPdfUrl so dev
 * (localhost:8000) and production (nginx-proxied relative path) resolve the
 * same way from every entry point.
 */

import { getSCGPdfUrl } from '../services/api';

const truncateQuery = (text, max = 80) => {
  if (!text) return '';
  const trimmed = String(text).trim();
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
};

/**
 * Pull rule render-time data out of either a flat rule object or a sourceInfo blob.
 * Returns the fields we need to build the viewer URL.
 */
export function extractRuleSource(ruleOrSourceInfo) {
  if (!ruleOrSourceInfo) return null;
  const src = ruleOrSourceInfo;
  // Unified extractor — handles three shapes:
  //   1. sourceInfo blob from GET /scg/rule/{id}/source (nested rule_details + top-level source_document)
  //   2. flat rule object from GET /scg/search (item/description/source_document all top-level)
  //   3. rule_a_full / rule_b_full embedded in conflict detail (flat shape)
  // Fall through to whichever shape provides each field.
  const rd = src.rule_details || src.rule_data || {};
  return {
    fileHash: src.source_document?.file_hash
      || rd.source_document?.file_hash
      || src.citation_display?.file_hash,
    pageNumber: src.location_metadata?.page_number
      || rd.location_metadata?.page_number,
    item: src.item
      || src.description
      || src.enhanced_description
      || rd.item
      || rd.description
      || rd.enhanced_description
      || src.location_metadata?.source_text
      || rd.location_metadata?.source_text
      || '',
    title: src.item || src.title || rd.item || rd.title || '',
    level: src.classification_level || rd.classification_level || '',
  };
}

/**
 * Build a /pdf-sandbox URL for a rule. Returns null when there's no PDF
 * available (e.g. rule has no source_document).
 *
 * @param {string} ruleId  raw SCG rule ID
 * @param {object|null} ruleOrSourceInfo  rule object OR sourceInfo blob (optional)
 */
export function buildPdfSandboxUrl(ruleId, ruleOrSourceInfo) {
  if (!ruleId) return null;
  const data = extractRuleSource(ruleOrSourceInfo);
  if (!data || !data.fileHash) return null;
  const params = new URLSearchParams();
  params.set('ruleId', ruleId);
  params.set('pdf', getSCGPdfUrl(data.fileHash));
  if (data.pageNumber) params.set('page', String(data.pageNumber));
  const q = truncateQuery(data.item, 80);
  if (q) params.set('q', q);
  if (data.title) params.set('title', truncateQuery(data.title, 120));
  if (data.level) params.set('level', data.level);
  return `/pdf-sandbox?${params.toString()}`;
}

/**
 * Returns 'available' when the rule has a source PDF to view, else null
 * (callers hide the button). The old 'bbox' | 'text-only' distinction is
 * gone along with the bbox overlay — text-search is the only highlight
 * strategy.
 */
export function pdfHighlightMode(ruleOrSourceInfo) {
  const data = extractRuleSource(ruleOrSourceInfo);
  if (!data || !data.fileHash) return null;
  return 'available';
}

/**
 * Build a /pdf-sandbox URL from an already-resolved PDF URL, for callers
 * that don't have a full SCG rule shape to feed buildPdfSandboxUrl —
 * e.g. rejected rules under review (no ruleId yet, they aren't persisted)
 * or a different rule corpus entirely (FOIA rule PDFs served from
 * /api/v1/foia/pdf/{hash} rather than the SCG pdf route). The sandbox
 * viewer only needs a PDF URL it can hand to react-pdf — it doesn't care
 * which backend route produced it.
 *
 * @param {object} opts
 * @param {string} opts.pdfUrl   required. Full/relative PDF URL (already
 *                                resolved, e.g. via getSCGPdfUrl or a raw
 *                                /api/v1/foia/pdf/{hash} path).
 * @param {number|string} [opts.page]
 * @param {string} [opts.title]  short label shown in the rule-context strip
 * @param {string} [opts.q]      search/highlight seed text
 * @param {string} [opts.level]  classification level for the IC badge
 * @param {string} [opts.ruleId] omit for rules that don't exist in the DB
 *                                yet (rejected rules) — ruleId gates the
 *                                context strip and a cosmetic filename fetch,
 *                                it isn't required for the PDF to render.
 */
export function buildPdfSandboxUrlFromParts({ pdfUrl, page, title, q, level, ruleId } = {}) {
  if (!pdfUrl) return null;
  const params = new URLSearchParams();
  params.set('pdf', pdfUrl);
  if (ruleId) params.set('ruleId', ruleId);
  if (page) params.set('page', String(page));
  const query = truncateQuery(q, 80);
  if (query) params.set('q', query);
  if (title) params.set('title', truncateQuery(title, 120));
  if (level) params.set('level', level);
  return `/pdf-sandbox?${params.toString()}`;
}
